<script lang="ts">
  import type { IdNameTypeTuple } from "../../models/common.interface";
  import { deleteSearchQueryStringParameter, setQueryStringParameter } from "../../url";
  import { SearchState } from "../../services/search-service";
  import type { SearchStateModel } from "../../models/search.interface";
  import StationSuggestion from "./StationSuggestion.svelte";
  import TooltipHybridButton from "./TooltipHybridButton.svelte";

  export let autocompleteUrl: URL;

  // added defaults in cases where params aren't provided
  export let title = "Station Search";
  export let tooltip = "Enter a station ID or name term and select from the autocomplete suggestions to search by station.";

  // moved to the tooltip button component
  //const tooltipContents = (tooltip && tooltip.startsWith("#") && document.getElementById(tooltip.substring(1))) ? document.getElementById(tooltip.substring(1)).innerHTML : tooltip;

  let stationsLoaded: Promise<IdNameTypeTuple[]>;

  let stationSearchInput: string;
  let params = [];

  /* Handling Autocomplete Inputs */
  let suggest = [];
  let hiLiteIndex = null;

  SearchState.subscribe((search: SearchStateModel) => {
    stationSearchInput = search.params["stations"] ? search.params["stations"].join() : "";
    params = search.params.getAll("stations") ? search.params.getAll("stations") : [];
  });

  async function handleStationInput() {
    if (!autocompleteUrl) return;

    const params = new URLSearchParams();
    params.set("text", stationSearchInput);
    stationsLoaded = fetch(`${autocompleteUrl}&${params}`)
      .then((resp) => resp.json())
      .then((suggestions) => suggest = suggestions.results);
  }

  // $: {
  //     if (stationsLoaded) {
  //         stationsLoaded.then(x => {
  //             console.log(x);
  //        });
  //     }
  // }

  /* navigation & highlighting in the suggestion dropdown */
  function navigateList(e) {
    if (e.key === "ArrowDown" && hiLiteIndex <= suggest.length - 1) {
      hiLiteIndex === null ? hiLiteIndex = 0 : hiLiteIndex += 1;

    } else if (e.key === "ArrowUp" && hiLiteIndex !== null) {
      hiLiteIndex === 0 ? hiLiteIndex = suggest.length - 1 : hiLiteIndex -= 1;

    } else if (e.key === "Enter") {
      if (hiLiteIndex !== null) {
        const suggestion = suggest[hiLiteIndex];
        /* check for null value */
        if (suggestion) {
          setParamVal(suggestion.id);
          handleSubmit(e);
        }
        e.preventDefault(); // Prevent default form submission
      }
      e.preventDefault();
    } else {
      return;
    }
  }

  /* grabs the text from the dropdown and places it into the Location Search input box */
  function setParamVal(suggestionId: string) {
    setQueryStringParameter("stations", suggestionId, true);

    clearSuggestions();
    hiLiteIndex = null;
  }

  function clearSuggestions(): void {
    suggest = [];
  }

  async function handleSubmit(e) {
    /* e.submitter is the targeted button */
    if (e.submitter) {
      setParamVal(e.submitter.id);
    }

    setTimeout(clearSuggestions, 1000);
  }

</script>

<svelte:window on:keydown={navigateList} />

<li class="mb-1">
  <div class="pb-2">
    {title}
    <TooltipHybridButton
      searchUrlQueryParam="station"
      tooltip={tooltip}
    />
  </div>
  <!-- station search input w/ floating label -->
  <form
    autocomplete="off"
    on:submit|preventDefault={handleSubmit}
  >
    <div class="form-floating margin-2">
      <input
        type="text"
        class="form-control nosubmit"
        id="floatingInputStation"
        placeholder="Station Search"
        bind:value={stationSearchInput}
        on:input={handleStationInput}>
      <label for="floatingInputStation">Stations</label>

      {#if suggest && suggest.length > 0}
        <ul class="suggestions list-group bg-white mt-1 small shadow-lg position-absolute">
          {#each suggest as s, i}
            <StationSuggestion suggestion={s} highlighted={i === hiLiteIndex} on:click={() => setParamVal(s.id)} />
          {/each}
        </ul>
      {/if}
    </div>
  </form>
  {#if $SearchState.params.has("stations")}
    {#each params as p}
      <div class="d-flex py-1">
        <span style="overflow-x: hidden; text-overflow: ellipsis"
              class="badge rounded-pill text-bg-dark pb-0 pe-3 pt-1"
              role="status"
              aria-label="station search pill: {p}"
        >
          <button
            type="button"
            id="delete-station-{p}"
            class="btn p-0 m-0"
            on:click={() => deleteSearchQueryStringParameter('stations', p)}
          >
              <i class="bi bi-x text-bg-dark" aria-label="Delete station {p}"></i>
          </button>
          <b> Id: </b> {p}
        </span>
      </div>
    {/each}
  {/if}
</li>

<style>
    .suggestions {
        z-index: 3;
    }
</style>
